/***
 *
 *   TICKET DESCRIPTION
 *   The request itself, at the top of the drawer's scroll so it can be read in
 *   full — it used to sit in a fixed band capped at ~6 lines, which squeezed
 *   the conversation and cut long descriptions off. Attachments now live in
 *   the rail (see attachments.js).
 *
 *   Collapsible; the choice is remembered per ticket so a realtime update
 *   doesn't re-expand it under the reader.
 *
 **********/

import React, { useState } from 'react';
import Style from './context.tailwind';
import { Icon, cn } from 'components/lib';
import { useTranslate } from 'app/translations';

const collapsedTickets = new Set();

const TicketDescription = ({ ticket }) => {
    const { _t } = useTranslate();
    const [, forceRender] = useState(0);
    const open = !collapsedTickets.has(ticket.id);

    const toggle = () => {
        if (open) collapsedTickets.add(ticket.id);
        else collapsedTickets.delete(ticket.id);
        forceRender((n) => n + 1);
    };

    const hasDescription = !!ticket.description?.trim();

    return (
        <section className={Style.section}>
            <button type="button" onClick={toggle} aria-expanded={open} className={Style.header}>
                <Icon
                    image="chevron-down"
                    size={14}
                    color="currentColor"
                    className={cn(Style.chevron, !open && Style.chevronClosed)}
                />
                <span className={Style.title}>{_t('ticket.detail.description')}</span>
                <span className={Style.toggleHint}>
                    {open ? _t('ticket.detail.collapse') : _t('ticket.detail.expand')}
                </span>
            </button>

            {open && (
                <div className={Style.body}>
                    {hasDescription ? (
                        <div
                            className={Style.descriptionText}
                            dangerouslySetInnerHTML={{
                                __html: formatDescription(ticket.description),
                            }}
                        />
                    ) : (
                        <p className={Style.descriptionEmpty}>{_t('ticket.detail.noDescription')}</p>
                    )}
                </div>
            )}
        </section>
    );
};

const escapeHtml = (text) =>
    text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

/**
 * Descriptions come from public forms and email — escape before linkifying so
 * the markup injected below is the only markup rendered.
 */
export function formatDescription(data) {
    if (!data) return '';
    let html = escapeHtml(data);
    html = html.replace(
        /((?:(?:https?|ftp):\/\/|www\.)[^\s<]+)/g,
        (url) => {
            const href = url.startsWith('www.') ? `https://${url}` : url;
            return `<a href="${href}" target="_blank" class="text-primary-600 underline-offset-2 hover:underline" rel="noreferrer">${url}</a>`;
        },
    );
    html = html.replace(
        /(^|[\s>])([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/g,
        '$1<a href="mailto:$2" class="text-primary-600">$2</a>',
    );
    return html.replace(/(?:\r\n|\r|\n)/g, '<br />');
}

export default TicketDescription;
